/**
 * CHANGE PASSWORD PAGE (TS-119) — cambio password obbligatorio.
 *
 * Ci si arriva solo per rimbalzo dall'AuthGuard, quando Liferay segnala
 * `passwordReset` sull'utente (flag "Require Password Reset", impostato dalla
 * password policy alla creazione o da un amministratore). L'utente è già
 * autenticato — il token c'è ed è valido — ma non deve poter usare il CRM
 * finché non cambia la password.
 *
 * Per questo la pagina sta FUORI dall'AppShell: niente sidebar, niente topbar,
 * stesso aspetto della login. Mostrare il guscio applicativo con i menu
 * disabilitati inviterebbe solo a cercare una scorciatoia che non c'è.
 */

import { useNavigate, useLocation, type Location } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useAppDispatch } from '../../store/hooks';
import { logout } from '../../auth/authSlice';
import { PasswordChangeForm } from '../../components/PasswordChangeForm';

export function ChangePasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Destinazione dopo il cambio: la location salvata dall'AuthGuard al
  // rimbalzo, con search e hash — stessa logica della LoginPage, perché è lo
  // stesso indirizzo che l'utente stava cercando di raggiungere.
  const fromLocation = (location.state as { from?: Location })?.from;
  const from = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search}${fromLocation.hash}`
    : '/tasks';

  return (
    <div className="login-page">
      <div className="login-brand">
        <img src="/logo.svg" alt={t('brand.name')} className="logo" width="100" />
        <h1>
          <span className="login-brand__accent">D'vel</span> CRM
        </h1>
      </div>

      <div className="login-form">
        <h2 className="text-center text-lg mb-md">{t('userSettings.security.changePassword')}</h2>
        <p className="text-muted text-center mb-lg text-sm">
          {t('auth.passwordResetRequired')}
        </p>

        <PasswordChangeForm
          required
          fullWidthSubmit
          onSuccess={() => navigate(from, { replace: true })}
        />

        {/*
          Via d'uscita: senza, chi non vuole (o non può) cambiare la password
          adesso resterebbe intrappolato su questa pagina, con la sola opzione
          di chiudere il browser. Dopo il logout è l'AuthGuard stesso a
          riportare alla login, perché lo status diventa 'unauthenticated'.
        */}
        <p className="text-center mt-lg text-sm">
          <Button variant="text" size="small" onClick={() => dispatch(logout())}>
            {t('common.logout')}
          </Button>
        </p>
      </div>
    </div>
  );
}
